import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../../Api/api';
import { getAuthToken } from '../../Api/authToken';
import { FiCalendar, FiUser, FiPhone, FiMail, FiCheckCircle, FiXCircle, FiClock, FiInbox } from 'react-icons/fi';
import './AdminPages.css';

const BookingList = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API_URL}/admin/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(res.data);
    } catch (err) {
      setError('Failed to load bookings');
      toast.error('Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id, status) => {
    setUpdatingId(id);
    try {
      const token = getAuthToken();
      await axios.put(`${API_URL}/admin/bookings/${id}`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Booking ${status}`);
      fetchBookings();
    } catch (err) {
      toast.error('Failed to update booking');
    } finally {
      setUpdatingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      pending:   { bg: '#fef3c7', color: '#92400e', icon: <FiClock size={12} /> },
      approved:  { bg: '#d1fae5', color: '#065f46', icon: <FiCheckCircle size={12} /> },
      rejected:  { bg: '#fee2e2', color: '#991b1b', icon: <FiXCircle size={12} /> },
      cancelled: { bg: '#f1f5f9', color: '#475569', icon: <FiXCircle size={12} /> },
    };
    const s = map[status] || map.pending;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '0.25rem 0.625rem', borderRadius: '1rem', fontSize: '0.75rem', fontWeight: 600, background: s.bg, color: s.color }}>
        {s.icon} {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="page-header"><h1>Bookings</h1></div>
        <div className="admin-card"><div className="skeleton" style={{ height: '400px', borderRadius: '8px' }}></div></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page">
        <div className="page-header"><h1>Bookings</h1></div>
        <div className="admin-card empty-state">
          <FiXCircle size={40} />
          <h3>{error}</h3>
          <button className="btn-primary" onClick={fetchBookings}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header flex-header">
        <div>
          <h1>Bookings</h1>
          <p>{bookings.length} total booking{bookings.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      <div className="admin-card table-wrapper">
        {bookings.length === 0 ? (
          <div className="empty-state">
            <FiInbox size={48} />
            <h3>No Bookings Yet</h3>
            <p>Bookings from the frontend will appear here.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-hybrid-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Customer</th>
                  <th>Property</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map(b => (
                  <tr key={b.id}>
                    <td><span className="hybrid-id">#{b.id}</span></td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <strong style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                          <FiUser size={13} /> {b.name}
                        </strong>
                        <span style={{ fontSize: '0.8rem', color: 'var(--admin-text-muted)', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                          <FiPhone size={12} /> {b.phone}
                        </span>
                        {b.email && (
                          <span style={{ fontSize: '0.8rem', color: 'var(--admin-text-muted)', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                            <FiMail size={12} /> {b.email}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <span className="info-pill" style={{ alignSelf: 'flex-start' }}>
                          Property #{b.property_id || 'N/A'}
                        </span>
                        {b.sub_register_name && (
                          <span style={{ fontSize: '0.8rem', color: 'var(--admin-text-main)' }}>
                            <strong>Sub-Register:</strong> {b.sub_register_name}
                          </span>
                        )}
                        {b.partner_name && (
                          <span style={{ fontSize: '0.8rem', color: 'var(--admin-text-main)' }}>
                            <strong>Partner:</strong> {b.partner_name}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>{getStatusBadge(b.status)}</td>
                    <td>
                      <span style={{ fontSize: '0.8rem', color: 'var(--admin-text-muted)', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                        <FiCalendar size={12} /> {new Date(b.createdAt).toLocaleDateString()}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
                        <select
                          className={`status-select status-booking-${b.status}`}
                          value={b.status}
                          disabled={updatingId === b.id}
                          onChange={(e) => handleStatusUpdate(b.id, e.target.value)}
                          style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem', borderRadius: '0.375rem' }}
                        >
                          <option value="pending">Pending</option>
                          <option value="approved">Approved</option>
                          <option value="rejected">Rejected</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingList;
